/*
 * <pb-bundle>: the "2+1 Gratis" trio builder (blocks/pb-bundle.liquid).
 *
 * The shopper fills three spots from the Designer and Niche collections; the cheapest is shown as
 * free. Prices here are a preview only. Add to cart puts the three products in the cart at their
 * normal prices and Shopify's automatic Buy X get Y discount makes the cheapest free.
 *
 * While "2+1" is selected this takes over the theme's add-to-cart button and the sticky bar
 * (which clicks that button): the label and price follow the trio, a click with spots still
 * empty opens the picker, and a full trio is added in one request instead of the single product.
 * Listeners sit on window in the capture phase so they run before the theme's own, which are
 * delegated from document (assets/component.js).
 */
import { CartAddEvent } from '@theme/events';

const ICON_X =
  '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M3.5 3.5l9 9m0-9l-9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

const ICON_PLUS =
  '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
const ICON_CHECK =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const escapeHtml = (text) =>
  String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/* Lower case, accents and punctuation dropped: the same folding as normalise() in pb-comparator-search.js. */
const normalise = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const fill = (template, values) => template.replace(/\[(\w+)\]/g, (_, key) => (key in values ? values[key] : ''));

/* "BEY540 - Inspired By Baccarat Rouge 540" -> code "BEY540", name "Inspired By Baccarat Rouge 540" */
const splitTitle = (title) => {
  const parts = title.replace(' – ', ' - ').split(' - ');
  return { code: parts[0].trim(), name: parts.slice(1).join(' - ').trim() };
};

/*
 * Money in the shopper's currency, modelled on how Liquid printed 1.234.567,89 for this market,
 * so symbol position and separators match the rest of the page.
 */
function moneyFormatter(sample) {
  const run = sample.match(/\d[\d.,\s '’]*\d/);
  if (!run) return (cents) => (cents / 100).toFixed(2);
  const digits = run[0];
  const tail = digits.match(/[^\d](\d{2})$/);
  const decimals = tail ? 2 : 0;
  const decimalSep = tail ? digits.charAt(digits.length - 3) : '';
  const thousandSep = /\d/.test(digits.charAt(1)) ? '' : digits.charAt(1);
  return (cents) => {
    const value = (cents / 100).toFixed(decimals);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    return sample.replace(digits, fraction ? grouped + decimalSep + fraction : grouped);
  };
}

class PbBundle extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.catalog = JSON.parse(this.querySelector('[data-catalog]').textContent).filter((p) => p.a);
    this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
    this.money = moneyFormatter(this.dataset.money || '');
    this.labels = { 1: this.dataset.label1, 2: this.dataset.label2 };
    this.byId = new Map(this.catalog.map((p) => [String(p.v), p]));
    this.refs = {};
    this.querySelectorAll('[data-ref]').forEach((el) => (this.refs[el.dataset.ref] = el));

    const current = this.byId.has(this.dataset.current) ? this.dataset.current : null;
    this.currentId = current;
    this.state = {
      mode: this.dataset.mode,
      slots: [current, null, null],
      order: [1, 0, 0],
      tick: 1,
      filter: 0,
      tab: 0,
      query: '',
      pick: null,
    };

    this.#bind();
    this.render();
  }

  disconnectedCallback() {
    this._abort?.abort();
    document.documentElement.classList.remove('pb-trio-on', 'pb-trio-pending');
  }

  /* ------------------------------------------------------------------ pricing */

  totals() {
    const { slots, order } = this.state;
    const chosen = slots.filter(Boolean).map((id) => this.byId.get(id));
    const full = chosen.reduce((sum, p) => sum + p.p, 0);
    let freeIndex = -1;
    if (chosen.length === 3) {
      // The cheapest is free; on a tie the one picked last carries the label (same saving).
      freeIndex = 0;
      slots.forEach((id, i) => {
        const price = this.byId.get(id).p;
        const best = this.byId.get(slots[freeIndex]).p;
        if (price < best || (price === best && order[i] > order[freeIndex])) freeIndex = i;
      });
    }
    const free = freeIndex >= 0 ? this.byId.get(slots[freeIndex]).p : 0;
    return { chosen, full, free, pay: full - free, freeIndex, left: 3 - chosen.length };
  }

  /* The lowest 2+1 total that includes this product, for "vanaf" while the trio is open. */
  fromPrice() {
    const prices = this.catalog.map((p) => p.p).sort((a, b) => a - b);
    const cheapest = prices[0] ?? 0;
    const anchor = this.currentId ? this.byId.get(this.currentId).p : cheapest;
    const trio = [anchor, cheapest, cheapest];
    return trio.reduce((a, b) => a + b, 0) - Math.min(...trio);
  }

  /* ------------------------------------------------------------------ rendering */

  render() {
    const { mode } = this.state;
    const t = this.totals();
    const bundle = mode === 'bundle';
    const s = this.strings;

    this.querySelectorAll('[data-select]').forEach((opt) =>
      opt.setAttribute('aria-checked', String(opt.dataset.select === mode))
    );
    document.documentElement.classList.toggle('pb-trio-on', bundle);
    document.documentElement.classList.toggle('pb-trio-pending', bundle && t.left > 0);

    this.refs.bundlePrice.innerHTML =
      t.left === 0
        ? `<span class="pb-bundle__now">${this.money(t.pay)}</span><s class="pb-bundle__was">${this.money(t.full)}</s>`
        : `<span class="pb-bundle__from">${escapeHtml(s.from)}</span><span class="pb-bundle__now">${this.money(this.fromPrice())}</span>`;
    if (this.refs.count) this.refs.count.textContent = fill(s.chosen, { n: 3 - t.left });

    this.#renderTrio(t);
    this.#renderRail(t);
    this.#renderSum(t);
    this.#syncButtons(t);
  }

  #renderTrio(t) {
    if (this.dataset.style !== 'tiles') return this.#renderList(t);
    const s = this.strings;
    const emptyCount = this.state.slots.filter((id) => !id).length;
    this.refs.trio.innerHTML = this.state.slots
      .map((id, i) => {
        if (!id) {
          const lastSpot = emptyCount === 1 || i === 2;
          return `<button class="pb-bundle__tile pb-bundle__tile--empty" type="button" data-pick="${i}">
            <span class="pb-bundle__frame"><span class="pb-bundle__plus" aria-hidden="true">+</span></span>
            <span class="pb-bundle__code">${escapeHtml(fill(s.slot, { n: i + 1 }))}</span>
            <span class="pb-bundle__name">${lastSpot ? `<span class="pb-bundle__hint-free">${escapeHtml(s.cheapest_free)}</span>` : escapeHtml(s.pick_scent)}</span>
          </button>`;
        }
        const p = this.byId.get(id);
        const { code, name } = splitTitle(p.t);
        const free = i === t.freeIndex;
        const removable = !(i === 0 && id === this.currentId);
        return `<div class="pb-bundle__tile${free ? ' is-free' : ''}">
          <span class="pb-bundle__frame">
            ${p.i ? `<img src="${escapeHtml(p.i)}" alt="" width="240" height="240" loading="lazy">` : ''}
            ${free ? `<span class="pb-bundle__flag">${escapeHtml(s.free)}</span>` : ''}
            ${removable ? `<button class="pb-bundle__x" type="button" data-remove="${i}" aria-label="${escapeHtml(fill(s.remove, { name: code }))}">${ICON_X}</button>` : ''}
          </span>
          <span class="pb-bundle__code">${escapeHtml(code)}</span>
          <span class="pb-bundle__name">${escapeHtml(name)}</span>
          <span class="pb-bundle__tile-price">${free ? `<s>${this.money(p.p)}</s><span class="is-free">${this.money(0)}</span>` : this.money(p.p)}</span>
          <button class="pb-bundle__swap" type="button" data-pick="${i}">${escapeHtml(s.change)}</button>
        </div>`;
      })
      .join('');
  }

  /* One row per spot, like the Kaching widget: picture and full title, or a plus and "Kies Geur". */
  #renderList(t) {
    const s = this.strings;
    this.refs.trio.innerHTML = this.state.slots
      .map((id, i) => {
        if (!id) {
          return `<div class="pb-bundle__row pb-bundle__row--empty">
            <button class="pb-bundle__row-plus" type="button" data-pick="${i}" aria-label="${escapeHtml(fill(s.slot, { n: i + 1 }))}"><span aria-hidden="true">+</span></button>
            <button class="pb-bundle__kies" type="button" data-pick="${i}">${escapeHtml(s.pick_button)}</button>
          </div>`;
        }
        const p = this.byId.get(id);
        const { code } = splitTitle(p.t);
        const free = i === t.freeIndex;
        const removable = !(i === 0 && id === this.currentId);
        return `<div class="pb-bundle__row${free ? ' is-free' : ''}">
          <span class="pb-bundle__row-img">${p.i ? `<img src="${escapeHtml(p.i.replace(/width=\d+/, 'width=96'))}" alt="" width="44" height="44" loading="lazy">` : ''}</span>
          <button class="pb-bundle__row-main" type="button" data-pick="${i}" aria-label="${escapeHtml(s.change)}: ${escapeHtml(p.t)}">
            <span class="pb-bundle__row-title">${escapeHtml(p.t)}</span>
          </button>
          <span class="pb-bundle__row-side">
            ${free ? `<span class="pb-bundle__row-tag">${escapeHtml(s.free)}</span>` : ''}
            ${removable ? `<button class="pb-bundle__x" type="button" data-remove="${i}" aria-label="${escapeHtml(fill(s.remove, { name: code }))}">${ICON_X}</button>` : ''}
          </span>
        </div>`;
      })
      .join('');
  }

  #renderRail(t) {
    if (!this.refs.rail) return; // quick add switched off in the block settings
    const { filter } = this.state;
    const full = t.left === 0;
    let list = this.catalog.filter((p) => String(p.v) !== this.currentId);
    if (filter === 0) {
      const best = list.filter((p) => p.b);
      if (best.length) list = best;
    } else {
      list = list.filter((p) => p.c === filter);
    }
    list = list.slice(0, Number(this.dataset.quickCount) || 12);

    this.refs.rail.innerHTML = list
      .map((p) => {
        const { code } = splitTitle(p.t);
        const img = p.i ? p.i.replace(/width=\d+/, 'width=96') : '';
        return `<button class="pb-bundle__mini" type="button" data-quick="${p.v}" ${full ? 'disabled' : ''} aria-label="${escapeHtml(fill(this.strings.add, { name: code }))}">
          ${img ? `<img src="${escapeHtml(img)}" alt="" width="40" height="40" loading="lazy">` : '<span></span>'}
          <span><span class="pb-bundle__mini-code">${escapeHtml(code)}</span><span class="pb-bundle__mini-price">${this.money(p.p)}</span></span>
          ${full ? '' : '<span class="pb-bundle__mini-add" aria-hidden="true">+</span>'}
        </button>`;
      })
      .join('');
    this.querySelectorAll('[data-filter]').forEach((b) =>
      b.setAttribute('aria-pressed', String(Number(b.dataset.filter) === filter))
    );
    this.refs.openAll.textContent = fill(this.strings.view_all, { n: this.catalog.length });
  }

  #renderSum(t) {
    if (!this.refs.sum) return; // summary switched off in the block settings
    const s = this.strings;
    const shipping = this.dataset.freeShipping === 'true' ? ` ${s.free_shipping}` : '';
    const percent = t.full ? Math.round((t.free / t.full) * 100) : 0;
    const done = t.left === 0;
    this.refs.sum.innerHTML = `
      <span class="pb-bundle__sum-label">${escapeHtml(s.total)}</span>
      <span class="pb-bundle__sum-total">${done ? `<s>${this.money(t.full)}</s>${this.money(t.pay)}` : this.money(t.full)}</span>
      <span class="pb-bundle__sum-save${done ? '' : ' is-muted'}">${escapeHtml(
        done ? fill(s.you_save, { amount: this.money(t.free), percent }) + shipping : fill(s.left, { n: t.left })
      )}</span>
      <span class="pb-bundle__meter${done ? ' is-done' : ''}">${[0, 1, 2]
        .map((k) => `<i class="${k < 3 - t.left ? 'is-on' : ''}"></i>`)
        .join('')}</span>`;
  }

  /* ------------------------------------------------------------------ the theme's buttons */

  #buttons() {
    const section = this.closest('.shopify-section') || document;
    return [...section.querySelectorAll('.product-details button[name="add"], .sticky-add-to-cart__button')];
  }

  #syncButtons(t) {
    const s = this.strings;
    const bundle = this.state.mode === 'bundle';
    for (const button of this.#buttons()) {
      const content = button.querySelector('.add-to-cart-text__content');
      const text = button.querySelector('.add-to-cart-text');
      if (!content || !text) continue;
      if (button.dataset.pbLabel === undefined) button.dataset.pbLabel = content.innerHTML;

      // The button shows only its label; the price stays in the price block above.
      let label = button.dataset.pbLabel;
      if (bundle && t.left > 0) label = `<span>${escapeHtml(fill(s.atc_choose, { n: t.left }))}</span>`;

      if (content.innerHTML !== label) content.innerHTML = label;
      text.querySelector('.pb-atc-price')?.remove();
    }
  }

  /* ------------------------------------------------------------------ picker */

  openPicker(index) {
    this.state.pick = index;
    this.refs.pickerTitle.textContent = fill(this.strings.picker_title, { n: index + 1 });
    this.#renderPicker();
    this.refs.picker.showModal();
    if (window.matchMedia('(hover: hover)').matches) this.refs.search.focus();
  }

  /*
   * Search works like the header search: titles and product tags (house, "Inspired by …", notes,
   * gender, codes such as "BR540"), with abbreviations ("LV", "YSL", "MFK") expanded by the same
   * expandQuery the header uses. It runs over the loaded catalog, so every match shows, not the
   * header dropdown's first ten. Title matches come first, then bestsellers.
   */
  #search(list) {
    const raw = this.state.query.trim();
    if (!raw) return list.sort((a, b) => Number(b.b) - Number(a.b));

    const queries = [raw];
    const expanded = this.expandQuery?.(raw);
    if (expanded && expanded !== raw) queries.push(expanded);
    const tokenSets = queries.map((q) => normalise(q).split(' ').filter(Boolean)).filter((set) => set.length);
    const hits = (text) => tokenSets.some((tokens) => tokens.every((token) => text.includes(token)));

    return list
      .map((p) => {
        p._title ??= normalise(p.t);
        p._all ??= normalise(`${p.t} ${p.k || ''}`);
        return { p, inTitle: hits(p._title), any: hits(p._all) };
      })
      .filter((r) => r.any)
      .sort((a, b) => Number(b.inTitle) - Number(a.inTitle) || Number(b.p.b) - Number(a.p.b))
      .map((r) => r.p);
  }

  #renderPicker() {
    const { tab, slots, pick } = this.state;
    const inTrio = new Set(slots.filter((id, i) => id && i !== pick));
    const list = this.#search(this.catalog.filter((p) => tab === 0 || p.c === tab));

    this.querySelectorAll('[data-tab]').forEach((b) =>
      b.setAttribute('aria-selected', String(Number(b.dataset.tab) === tab))
    );
    this.refs.grid.innerHTML = list.length
      ? list
          .map((p) => {
            const { code, name } = splitTitle(p.t);
            const isIn = inTrio.has(String(p.v));
            const label = isIn ? `${p.t}, ${this.strings.in_trio}` : `${this.strings.choose} ${p.t}`;
            return `<button class="pb-bundle__card${isIn ? ' is-in' : ''}" type="button" data-choose="${p.v}" aria-label="${escapeHtml(label)}">
              <span class="pb-bundle__card-img">
                ${p.i ? `<img src="${escapeHtml(p.i)}" alt="" width="240" height="300" loading="lazy">` : ''}
                <span class="pb-bundle__card-add" aria-hidden="true">${isIn ? ICON_CHECK : ICON_PLUS}</span>
              </span>
              <span class="pb-bundle__card-text">
                ${p.b ? `<span class="pb-bundle__card-flag">${escapeHtml(this.dataset.bestseller || 'Bestseller')}</span>` : ''}
                <span class="pb-bundle__card-code">${escapeHtml(code)}</span>
                <span class="pb-bundle__card-name">${escapeHtml(name)}</span>
                <span class="pb-bundle__card-price">${this.money(p.p)}</span>
              </span>
            </button>`;
          })
          .join('')
      : `<p class="pb-bundle__empty">${escapeHtml(this.strings.no_results)}</p>`;
    this.refs.pickerCount.textContent = fill(this.strings.count, { n: list.length });
  }

  #place(index, id) {
    const { state } = this;
    state.slots[index] = String(id);
    state.order[index] = ++state.tick;
    this.render();
  }

  #nextEmpty() {
    return this.state.slots.findIndex((id) => !id);
  }

  /* ------------------------------------------------------------------ add to cart */

  async #addTrio(button) {
    const t = this.totals();
    const counts = new Map();
    for (const id of this.state.slots) counts.set(id, (counts.get(id) || 0) + 1);
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    button?.setAttribute('aria-busy', 'true');
    this.refs.error.hidden = true;
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [...counts].map(([id, quantity]) => ({ id: Number(id), quantity })),
          sections: sections.join(','),
        }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message);
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.dispatchEvent(
        new CartAddEvent(cart, this.id || 'pb-bundle', {
          source: 'pb-bundle',
          itemCount: t.chosen.length,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      this.refs.error.textContent = error.message || this.strings.error;
      this.refs.error.hidden = false;
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  /* ------------------------------------------------------------------ events */

  #bind() {
    this._abort = new AbortController();
    const { signal } = this._abort;
    const { state } = this;

    /*
     * Focus rings for keyboard users only. Safari on iPhone shows a ring on a tapped option card
     * and on the button the picker hands focus back to, so data-pointer marks touch and mouse
     * use and the stylesheet hides the rings while it is set; a key press brings them back.
     */
    this.addEventListener('pointerdown', () => this.setAttribute('data-pointer', ''), { signal });
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') this.removeAttribute('data-pointer');
      },
      { signal, capture: true }
    );

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest(
          '[data-select],[data-pick],[data-remove],[data-quick],[data-filter],[data-tab],[data-choose],[data-ref="openAll"],[data-ref="close"]'
        );
        if (!target || !this.contains(target)) return;

        if (target.matches('[data-remove]')) {
          state.slots[Number(target.dataset.remove)] = null;
          this.render();
        } else if (target.matches('[data-pick]')) {
          this.openPicker(Number(target.dataset.pick));
        } else if (target.matches('[data-quick]')) {
          const next = this.#nextEmpty();
          if (next >= 0) this.#place(next, target.dataset.quick);
        } else if (target.matches('[data-filter]')) {
          state.filter = Number(target.dataset.filter);
          this.render();
        } else if (target.matches('[data-tab]')) {
          state.tab = Number(target.dataset.tab);
          this.#renderPicker();
        } else if (target.matches('[data-choose]')) {
          const index = state.pick;
          this.refs.picker.close();
          this.#place(index, target.dataset.choose);
          const next = this.#nextEmpty();
          if (next >= 0 && !this.hasAttribute('data-pointer')) {
            this.refs.trio.querySelector(`[data-pick="${next}"]`)?.focus();
          }
        } else if (target.dataset.ref === 'openAll') {
          const next = this.#nextEmpty();
          this.openPicker(next >= 0 ? next : 2);
        } else if (target.dataset.ref === 'close') {
          this.refs.picker.close();
        } else if (target.matches('[data-select]') && state.mode !== target.dataset.select) {
          state.mode = target.dataset.select;
          this.render();
        }
      },
      { signal }
    );

    this.addEventListener(
      'keydown',
      (event) => {
        const opt = event.target.closest?.('[data-select]');
        if (opt && event.target === opt && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          opt.click();
        }
      },
      { signal }
    );

    this.refs.search.addEventListener(
      'input',
      () => {
        state.query = this.refs.search.value;
        this.#renderPicker();
        // The header's abbreviation table loads on the first keystroke, then the results redraw.
        if (!this.expandQuery && !this._loadingAliases) {
          this._loadingAliases = import('@theme/search-alias')
            .then(({ expandQuery }) => {
              this.expandQuery = expandQuery;
              this.#renderPicker();
            })
            .catch(() => {});
        }
      },
      { signal }
    );

    // A click on the backdrop closes the picker.
    this.refs.picker.addEventListener(
      'click',
      (event) => {
        if (event.target === this.refs.picker) this.refs.picker.close();
      },
      { signal }
    );

    const section = this.closest('.shopify-section') || document;

    // Spots still empty: the button opens the picker instead of adding.
    window.addEventListener(
      'click',
      (event) => {
        if (state.mode !== 'bundle') return;
        const button = event.target.closest?.('button[name="add"], .sticky-add-to-cart__button');
        if (!button || !section.contains(button)) return;
        const next = this.#nextEmpty();
        if (next < 0) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openPicker(next);
      },
      { capture: true, signal }
    );

    // A full trio: add all three instead of the single product.
    window.addEventListener(
      'submit',
      (event) => {
        if (state.mode !== 'bundle' || this.#nextEmpty() >= 0) return;
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component') || !section.contains(form)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#addTrio(form.querySelector('button[name="add"]'));
      },
      { capture: true, signal }
    );

    // The sticky bar is rendered after this block; label it once it exists.
    const sticky = section.querySelector('sticky-add-to-cart');
    if (sticky) {
      new MutationObserver(() => this.#syncButtons(this.totals())).observe(sticky, { childList: true, subtree: false });
    }
  }
}

if (!customElements.get('pb-bundle')) customElements.define('pb-bundle', PbBundle);
